import React, { useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import Select from 'react-select';
import { v4 as uuid } from 'uuid';
import axios from 'axios';

import { useGlobalContext } from '../../../Context';
import useDiscussionsClubsHostsInternalHosts from '../../../hooks/useDiscussionsClubsHostsInternalHosts';
import { globalURLS } from '../../../Global/urls';

type HostOption = { value: string; label: string; image: string };

type DebateFaculty = {
    facultyid: string;
    faculty: string;
    facultyDefaultImage: string;
};

type DebateModerator = {
    accountid: string;
    name: string;
    moderatorImage: string;
};

type DebateOption = {
    rank: number;
    option: string;
    colorLight: string;
    colorDark: string;
    ctaLight: string;
    ctaDark: string;
    faculties: DebateFaculty[];
};

type DebateRound = {
    id: string;
    rank: number;
    name: string;
    duration: number;
    hideAfterMillis: number;
    moderators: DebateModerator[];
    options: DebateOption[];
};

const optionColors = (rank: number): Omit<DebateOption, 'rank' | 'option'> =>
    rank === 1
        ? {
              colorLight: '#2F6FED',
              colorDark: '#8EB4FF',
              ctaLight: '#FFFFFF',
              ctaDark: '#102A56',
          }
        : {
              colorLight: '#E5484D',
              colorDark: '#FF8A8A',
              ctaLight: '#FFFFFF',
              ctaDark: '#3D1214',
          };

const blankOption = (rank: number): DebateOption => ({
    rank,
    option: '',
    ...optionColors(rank),
    faculties: [],
});

const blankRound = (rank: number): DebateRound => ({
    id: uuid(),
    rank,
    name: '',
    duration: 30,
    hideAfterMillis: 10000,
    moderators: [],
    options: [blankOption(1), blankOption(2)],
});

const toHostOptions = (
    rows: { accountid: string; name: string; image?: string }[] = [],
): HostOption[] =>
    rows.map(row => ({
        value: row.accountid,
        label: row.name || row.accountid,
        image: row.image || '',
    }));

const hostToFaculty = (host: HostOption): DebateFaculty => ({
    facultyid: host.value,
    faculty: host.label,
    facultyDefaultImage: host.image || '',
});

const hostToModerator = (host: HostOption): DebateModerator => ({
    accountid: host.value,
    name: host.label,
    moderatorImage: host.image || '',
});

const colorFields: { key: keyof DebateOption; label: string }[] = [
    { key: 'colorLight', label: 'Light' },
    { key: 'colorDark', label: 'Dark' },
    { key: 'ctaLight', label: 'CTA light' },
    { key: 'ctaDark', label: 'CTA dark' },
];

const selectMenuPortalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed' as const,
    styles: {
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    },
};

const AddDebate: React.FC<{ show: boolean; onHide(): void }> = ({
    show,
    onHide,
}) => {
    const [discussion, setDiscussion] = useState<HostOption | null>(null);
    const [rounds, setRounds] = useState<DebateRound[]>([]);
    const [hosts, setHosts] = useState<HostOption[]>([]);
    const [featuredHosts, setFeaturedHosts] = useState<HostOption[]>([]);
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState('');

    const { selectDiscussions } = useDiscussionsClubsHostsInternalHosts({
        getSelectDiscussions: true,
    });
    const { setShowSuccessToast, setShowErrorToast } = useGlobalContext();

    const updateRound = (index: number, patch: Partial<DebateRound>) => {
        setRounds(prev =>
            prev.map((round, i) => (i === index ? { ...round, ...patch } : round)),
        );
    };

    const updateOption = (
        roundIndex: number,
        optionIndex: number,
        patch: Partial<DebateOption>,
    ) => {
        setRounds(prev =>
            prev.map((round, i) => {
                if (i !== roundIndex) return round;
                const options = round.options.map((option, j) =>
                    j === optionIndex ? { ...option, ...patch } : option,
                );
                return { ...round, options };
            }),
        );
    };

    const setOptionFaculties = (
        roundIndex: number,
        optionIndex: number,
        selected: readonly HostOption[] | null,
    ) => {
        updateOption(roundIndex, optionIndex, {
            faculties: (selected || []).map(hostToFaculty),
        });
    };

    const fetchDebate = async (selected: HostOption) => {
        setRounds([]);
        setHosts([]);
        setFeaturedHosts([]);
        setFormError('');
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}getDebate`,
                { discussionid: selected.value },
                globalURLS.config,
            );
            if (resp?.data?.success !== true) {
                setShowErrorToast(true);
                return;
            }
            const data = resp.data.data || {};
            const nextHosts = toHostOptions(data.hosts);
            const nextFeatured = toHostOptions(data.featuredHosts);
            setHosts(nextHosts);
            setFeaturedHosts(nextFeatured);
            const existing = Array.isArray(data.rounds) ? data.rounds : [];
            setRounds(
                existing.length
                    ? existing.map((round: DebateRound, index: number) => ({
                          ...blankRound(index + 1),
                          ...round,
                          id: round.id || uuid(),
                          rank: index + 1,
                          duration: Number(round.duration) || 30,
                          hideAfterMillis: Number(round.hideAfterMillis) || 10000,
                          moderators: (round.moderators || []).map(moderator => ({
                              accountid: moderator.accountid,
                              name: moderator.name || '',
                              moderatorImage: moderator.moderatorImage || '',
                          })),
                          options: [0, 1].map(i => ({
                              ...blankOption(i + 1),
                              ...(round.options?.[i] || {}),
                              rank: i + 1,
                              faculties: (round.options?.[i]?.faculties || [])
                                  .filter(faculty => faculty.facultyid)
                                  .map(faculty => ({
                                      facultyid: faculty.facultyid,
                                      faculty: faculty.faculty || '',
                                      facultyDefaultImage: faculty.facultyDefaultImage || '',
                                  })),
                          })),
                      }))
                    : [],
            );
        } catch (err) {
            setShowErrorToast(true);
        }
    };

    const handleClose = () => {
        setDiscussion(null);
        setRounds([]);
        setHosts([]);
        setFeaturedHosts([]);
        setValidated(false);
        setFormError('');
        onHide();
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setFormError('');

        if (!discussion?.value) {
            setFormError('Select a discussion');
            return;
        }

        const invalid = rounds.find(
            round =>
                !round.name.trim() ||
                !round.moderators.length ||
                round.options.some(
                    option => !option.option.trim() || !option.faculties.length,
                ),
        );
        if (invalid || event.currentTarget.checkValidity() === false) {
            setValidated(true);
            setFormError(
                'Each round needs a title, at least one moderator, and an option title with at least one faculty on each side.',
            );
            return;
        }

        try {
            setIsLoading(true);
            const res = await axios.post(
                `${globalURLS.baseURL}saveDebate`,
                {
                    discussionid: discussion.value,
                    rounds: rounds.map((round, index) => ({
                        ...round,
                        rank: index + 1,
                        name: round.name.trim(),
                        duration: Number(round.duration) || 30,
                        hideAfterMillis: Number(round.hideAfterMillis) || 10000,
                        moderators: round.moderators,
                        options: round.options.map((option, optionIndex) => ({
                            rank: optionIndex + 1,
                            option: option.option.trim(),
                            colorLight: option.colorLight,
                            colorDark: option.colorDark,
                            ctaLight: option.ctaLight,
                            ctaDark: option.ctaDark,
                            faculties: option.faculties,
                        })),
                    })),
                },
                globalURLS.config,
            );
            if (res?.data?.success === true) {
                handleClose();
                setShowSuccessToast(true);
            } else {
                setFormError(res?.data?.message || 'Could not save debate rounds');
                setShowErrorToast(true);
            }
        } catch (err) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message || 'Could not save debate rounds';
            setFormError(message);
            setShowErrorToast(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" scrollable animation={false}>
            <Modal.Header closeButton>
                <Modal.Title>Debate</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form id="debate-form" noValidate validated={validated} onSubmit={onSubmit}>
                    <Form.Label>Discussion</Form.Label>
                    <Select
                        closeMenuOnSelect
                        onChange={(val: HostOption | null) => {
                            setDiscussion(val);
                            if (val?.value) fetchDebate(val);
                        }}
                        isSearchable
                        options={selectDiscussions || []}
                        value={discussion}
                        {...selectMenuPortalProps}
                    />

                    {discussion?.value &&
                        rounds.map((round, roundIndex) => (
                            <div
                                key={round.id}
                                className="border rounded p-3 mt-4"
                                style={{ backgroundColor: '#F8F9FA' }}
                            >
                                <h5>Round {roundIndex + 1}</h5>
                                <Form.Group className="mb-3">
                                    <Form.Label>Round title</Form.Label>
                                    <Form.Control
                                        required
                                        value={round.name}
                                        placeholder="Round title"
                                        onChange={e =>
                                            updateRound(roundIndex, { name: e.target.value })
                                        }
                                    />
                                </Form.Group>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Duration (seconds)</Form.Label>
                                            <Form.Control
                                                required
                                                type="number"
                                                min={1}
                                                value={round.duration}
                                                onChange={e =>
                                                    updateRound(roundIndex, {
                                                        duration: Number(e.target.value),
                                                    })
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Hide after (ms)</Form.Label>
                                            <Form.Control
                                                required
                                                type="number"
                                                min={0}
                                                value={round.hideAfterMillis}
                                                onChange={e =>
                                                    updateRound(roundIndex, {
                                                        hideAfterMillis: Number(e.target.value),
                                                    })
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Moderators</Form.Label>
                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        isSearchable
                                        options={hosts}
                                        placeholder={
                                            hosts.length
                                                ? 'Select hosts'
                                                : 'No hosts on this discussion'
                                        }
                                        value={round.moderators.map(
                                            moderator =>
                                                hosts.find(host => host.value === moderator.accountid) || {
                                                    value: moderator.accountid,
                                                    label: moderator.name || moderator.accountid,
                                                    image: moderator.moderatorImage || '',
                                                },
                                        )}
                                        onChange={(vals: readonly HostOption[] | null) =>
                                            updateRound(roundIndex, {
                                                moderators: (vals || []).map(hostToModerator),
                                            })
                                        }
                                        {...selectMenuPortalProps}
                                    />
                                </Form.Group>

                                {[0, 1].map(side => (
                                    <div key={side} className="border rounded p-3 mb-3 bg-white">
                                        <Form.Label>Option {side + 1} faculties</Form.Label>
                                        <Select
                                            isMulti
                                            className="mb-3"
                                            closeMenuOnSelect={false}
                                            isSearchable
                                            options={featuredHosts}
                                            placeholder={
                                                featuredHosts.length
                                                    ? 'Select featured hosts'
                                                    : 'No featured hosts on this discussion'
                                            }
                                            value={(round.options[side]?.faculties || []).map(
                                                faculty =>
                                                    featuredHosts.find(
                                                        host => host.value === faculty.facultyid,
                                                    ) || {
                                                        value: faculty.facultyid,
                                                        label: faculty.faculty || faculty.facultyid,
                                                        image: faculty.facultyDefaultImage || '',
                                                    },
                                            )}
                                            onChange={(vals: readonly HostOption[] | null) =>
                                                setOptionFaculties(roundIndex, side, vals)
                                            }
                                            {...selectMenuPortalProps}
                                        />
                                        <Form.Group className="mb-3">
                                            <Form.Label>Option {side + 1}</Form.Label>
                                            <Form.Control
                                                required
                                                value={round.options[side]?.option || ''}
                                                placeholder="Option"
                                                onChange={e =>
                                                    updateOption(roundIndex, side, {
                                                        option: e.target.value,
                                                    })
                                                }
                                            />
                                        </Form.Group>
                                        <Row>
                                            {colorFields.map(field => (
                                                <Col xs={6} md={3} key={field.key} className="mb-2">
                                                    <Form.Label>{field.label}</Form.Label>
                                                    <Form.Control
                                                        type="color"
                                                        value={
                                                            (round.options[side]?.[field.key] as string) ||
                                                            '#000000'
                                                        }
                                                        onChange={e =>
                                                            updateOption(roundIndex, side, {
                                                                [field.key]: e.target.value,
                                                            })
                                                        }
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                ))}

                                <Button
                                    variant="danger"
                                    type="button"
                                    onClick={() =>
                                        setRounds(prev => prev.filter((_, i) => i !== roundIndex))
                                    }
                                >
                                    Remove round
                                </Button>
                            </div>
                        ))}

                    {discussion?.value && (
                        <Button
                            className="mt-3"
                            type="button"
                            onClick={() =>
                                setRounds(prev => [...prev, blankRound(prev.length + 1)])
                            }
                        >
                            Add round
                        </Button>
                    )}

                    {formError && (
                        <div className="text-danger mt-3">{formError}</div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" type="button" onClick={handleClose}>
                    Close
                </Button>
                {discussion?.value && (
                    <Button
                        form="debate-form"
                        variant={isLoading ? 'warning' : 'success'}
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Save rounds'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AddDebate;
